import type { Logger } from "@workleap/logging";
import mixpanel from "mixpanel-browser";

/**
 * `api_host` + `lib_base_path` is the documented way to point the SDK at a proxy, but
 * `lib_base_path` is missing from mixpanel-browser 2.82.1's `Config` type. Augmenting beats a cast;
 * it stays inline because a standalone .d.ts is not loaded by apps compiling this source.
 * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#step-2-point-the-javascript-sdk-at-your-proxy
 */
declare module "mixpanel-browser" {
    interface Config {
        lib_base_path: string;
    }
}

/**
 * The proxy swaps in the real project token server-side, so the value we send is never used and the
 * frontend bundle carries no credential. It still has to be non-empty: the SDK derives its
 * persistence cookie name (`mp_<token>_mixpanel`) from it.
 */
const PROXY_PLACEHOLDER_TOKEN = "sg-protect-proxied";

let isInitialized = false;
let logger: Logger | undefined;

export interface SessionReplayOptions {
    /** Base URL of the ShareGate auth proxy route that fronts the Mixpanel API. */
    apiHost: string;
    /** Base URL the proxy serves the Mixpanel bundles from, see the note in `initSessionReplay`. */
    libBasePath: string;
}

/**
 * Starts Mixpanel Session Replay.
 *
 * This is intentionally a side implementation living next to the regular Mixpanel tracking, which
 * goes through `@workleap/telemetry` and our own `/bff/telemetry/track-single-event` endpoint.
 * Session replay is client-side only, so it needs the Mixpanel JavaScript SDK, but it must not take
 * over event tracking: page views and interactions keep flowing through the backend endpoint.
 */
export function initSessionReplay({ apiHost, libBasePath }: SessionReplayOptions, replayLogger?: Logger) {
    if (isInitialized || !apiHost) {
        return;
    }

    logger = replayLogger;

    try {
        mixpanel.init(PROXY_PLACEHOLDER_TOKEN, {
            // Recordings go through the ShareGate auth proxy rather than straight to Mixpanel. Ad
            // blockers filter the Mixpanel hosts, which cost us an estimated 30-50% of sessions; a
            // first-party host is not filtered, and the proxy holds the real token so we don't ship it.
            api_host: apiHost,
            // `api_payload_format` only defaults to JSON when `api_host` matches /\.mixpanel\.com/.
            // Against our own host it would silently fall back to base64 form-encoding.
            api_payload_format: "json",
            // Unused today: the default `mixpanel-browser` entry statically bundles the recorder and
            // loads it with a no-op loader, so nothing is fetched from this path. It is set so that if
            // the entry point ever changes to an async build, the recorder comes from the proxy rather
            // than cdn.mxpnl.com. That loader sets `onload` but no `onerror`, so a blocked bundle
            // would stop replay with no error anywhere. Don't remove it as dead config, and don't
            // narrow it to `recorder_src`: the SDK appends its own per-release hashed filename
            // (e.g. "mixpanel-recorder-BbPxtaqp.js"), so a pinned filename would break on upgrade.
            lib_base_path: libBasePath,
            record_sessions_percent: 100,
            // Same convention as LogRocket: everything is masked unless it's explicitly marked
            // "data-public". See .claude/rules/session-replay-privacy.md.
            record_mask_all_text: true,
            record_mask_all_inputs: true,
            record_unmask_text_selector: "[data-public]",
            record_unmask_input_selector: "[data-public]",
            // Setting this replaces the SDK default, so keep "img, video, audio" in the string.
            record_block_selector: "img, video, audio, [data-replay-hidden]",
            // Page views and clicks are already tracked through the backend endpoint, letting the
            // SDK capture them too would duplicate the events and bill them a second time.
            track_pageview: false,
            autocapture: false
        });

        isInitialized = true;
        logger?.information("[SessionReplay] Mixpanel session replay initialized.");
    } catch {
        logger?.error("[SessionReplay] Failed to initialize Mixpanel session replay.");
    }
}

/**
 * Associates the current recording with the same user the tracking events are attributed to.
 *
 * The `distinctId` must match the "distinct_id" of the events sent to
 * `/bff/telemetry/track-single-event`, otherwise the replay will land on a different Mixpanel user
 * than the events explaining it.
 */
export function identifySessionReplayUser(distinctId: string) {
    if (!isInitialized || !distinctId) {
        return;
    }

    try {
        mixpanel.identify(distinctId);
        logger?.information(`[SessionReplay] Identified user: ${distinctId}`);
    } catch {
        logger?.error("[SessionReplay] Failed to identify user.");
    }
}
